/**
 * Phase 10D: Calendar grid with react-big-calendar. Events = renewals on expiry date.
 * Dot color by urgency; click shows popover with policies for that day.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Popover, Typography, Button, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import ar from 'date-fns/locale/ar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { daysUntil, getUrgencyLevel } from '../../utils/renewalHelpers';
import t from '../../utils/renewalsTranslations';

const locales = { 'en-US': enUS, en: enUS, ar };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { locale: enUS }),
  getDay,
  locales,
});

function getUrgencyColor(days) {
  const level = getUrgencyLevel(days);
  if (level === 'expired' || level === 'critical') return '#d32f2f';
  if (level === 'urgent' || level === 'high') return '#ed6c02';
  if (level === 'medium' || level === 'low') return '#1976d2';
  return '#9e9e9e';
}

export default function RenewalCalendarGrid({ renewals = [], locale = 'en', onSelectEvent }) {
  const navigate = useNavigate();
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [anchorPos, setAnchorPos] = useState({ top: 0, left: 0 });

  const events = useMemo(() => {
    return (renewals || []).map((r) => {
      const d = r.expiryDate || r.end_date;
      const date = d ? new Date(d) : new Date();
      const days = r.daysUntilExpiry ?? daysUntil(d);
      return {
        ...r,
        start: date,
        end: date,
        title: r.policyName || r.policy_name || 'Policy',
        daysUntilExpiry: days,
        policyId: r.policyId || r.id,
      };
    });
  }, [renewals]);

  const handleSelectEvent = (event) => {
    if (onSelectEvent) onSelectEvent(event);
    else navigate(`/renewals/${event.policyId || event.id}`);
  };

  const handleSelectSlot = (slotInfo) => {
    const dayStart = new Date(slotInfo.start);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(slotInfo.start);
    dayEnd.setHours(23, 59, 59, 999);
    const onDay = events.filter((e) => e.start >= dayStart && e.start <= dayEnd);
    if (onDay.length > 0) {
      setSelectedDateEvents(onDay);
      setAnchorPos({ top: 180, left: 240 });
    }
  };

  const handleClosePopover = () => {
    setSelectedDateEvents([]);
  };

  const eventPropGetter = (event) => ({
    style: {
      backgroundColor: getUrgencyColor(event.daysUntilExpiry),
      border: 'none',
      borderRadius: '50%',
      width: 8,
      height: 8,
      minHeight: 8,
    },
  });

  const open = selectedDateEvents.length > 0;

  return (
    <Box sx={{ height: '100%', '& .rbc-calendar': { fontFamily: 'inherit' } }}>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlot}
        selectable
        eventPropGetter={eventPropGetter}
        views={['month', 'agenda']}
        view="month"
        defaultView="month"
        rtl={locale === 'ar'}
        culture={locale}
        popup
        toolbar
        style={{ height: '100%' }}
      />
      <Popover
        open={open}
        onClose={handleClosePopover}
        anchorReference="anchorPosition"
        anchorPosition={open ? anchorPos : undefined}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('renewals.calendar.policiesOnDate') || 'Policies expiring this day'}
          </Typography>
          <List dense>
            {selectedDateEvents.map((ev) => (
              <ListItem key={ev.policyId || ev.id} disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleClosePopover();
                    navigate(`/renewals/${ev.policyId || ev.id}`);
                  }}
                >
                  <ListItemText
                    primary={ev.policyName || ev.policy_name || 'Policy'}
                    secondary={`${ev.insurerName || ev.insurer_name || ''} · ${t('renewals.card.daysLeft')} ${ev.daysUntilExpiry ?? ''}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Button size="small" onClick={handleClosePopover}>
            {t('renewals.share.cancel')}
          </Button>
        </Box>
      </Popover>
      <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#d32f2f' }} /> {t('renewals.calendar.legendCritical') || '<30 days'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ed6c02' }} /> {t('renewals.calendar.legendUrgent') || '31-60'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1976d2' }} /> {t('renewals.calendar.legendMedium') || '61-90'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9e9e9e' }} /> {t('renewals.calendar.legendGrey') || '>90'}
        </Typography>
      </Box>
    </Box>
  );
}
